/**
 * Store for managing the proctor system configuration object.
 * @type {FluxibleStore}
 */

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import createStore from 'vendor/cnpm/fluxible.v0-4/addons/createStore';

import ValidationErrors from 'bundles/author-course/constants/ProctorConfigValidationErrorTypes';
import type { ProctorConfigurationDraft } from 'bundles/authoring/common/types/proctorConfig';

const DEFAULT_ATTEMPT_CONFIG = { maxSubmissions: 1 };
const DEFAULT_ATTEMPT_CONFIG_TIME_LIMIT = {
  gracePeriod: 30000,
  duration: 3600000,
  lateSubmissionPolicy: {
    'org.coursera.proctor.configuration.MustSubmitLastSavedVersionAtTimeLimit': {},
  },
};

const ProctorConfigStore = createStore({
  storeName: 'ProctorConfigStore',

  initialize() {
    this.id = null;
    this.proctorConfigDraftId = null;
    this.proctorConfigDraft = {};
    this.proctorConfigDraftMetadata = {};
    this.proctorConfigDraftErrors = [];
    this.draftHasChanges = false;
  },

  isLoaded() {
    return !!this.proctorConfigDraftId;
  },

  hasErrors() {
    return this.proctorConfigDraftErrors.length > 0;
  },

  getErrors() {
    return this.proctorConfigDraftErrors;
  },

  hasChanges() {
    return this.draftHasChanges;
  },

  getId() {
    return this.id;
  },

  getDraftId() {
    return this.proctorConfigDraftId;
  },

  getDraft() {
    return this.proctorConfigDraft;
  },

  getDraftMetadata() {
    return this.proctorConfigDraftMetadata;
  },

  getDraftMaxAttempts() {
    return this.proctorConfigDraft.maxAttempts;
  },

  getDraftMaxSubmissions() {
    if (this.proctorConfigDraft.attemptConfigurations && this.proctorConfigDraft.attemptConfigurations[0]) {
      return this.proctorConfigDraft.attemptConfigurations[0].maxSubmissions;
    } else {
      return null;
    }
  },

  getDraftTimeLimit() {
    if (
      this.proctorConfigDraft.attemptConfigurations &&
      this.proctorConfigDraft.attemptConfigurations[0] &&
      this.proctorConfigDraft.attemptConfigurations[0].timeLimit
    ) {
      return this.proctorConfigDraft.attemptConfigurations[0].timeLimit.duration;
    } else {
      return null;
    }
  },

  validateDraft() {
    this.proctorConfigDraftErrors = [];

    // note: hasOwnProperty check because a config without "maxAttempts" key/val is valid
    // allow maxAttempts value of 0
    if (
      Object.prototype.hasOwnProperty.call(this.proctorConfigDraft, 'maxAttempts') &&
      (this.proctorConfigDraft.maxAttempts === undefined ||
        this.proctorConfigDraft.maxAttempts < 0 ||
        this.proctorConfigDraft.maxAttempts > 99)
    ) {
      this.proctorConfigDraftErrors.push(ValidationErrors.PROCTOR_MAX_ATTEMPTS_INVALID);
    }
  },
});

type ReceiveProctorConfigDraftProps = {
  proctorConfigDraft: ProctorConfigurationDraft;
  resetChanges?: boolean;
  hasChanges?: boolean;
};

ProctorConfigStore.handlers = {
  RECEIVE_PROCTOR_CONFIG_DRAFT({ proctorConfigDraft, resetChanges, hasChanges }: ReceiveProctorConfigDraftProps) {
    this.id = proctorConfigDraft.id;
    this.proctorConfigDraftId = proctorConfigDraft.proctorConfigurationId;
    this.proctorConfigDraft = proctorConfigDraft.draft || {};
    this.proctorConfigDraftMetadata = proctorConfigDraft.metadata || {};

    // only update this.draftHasChanges if hasChanges is defined, and we are not resetting the changes
    if (!resetChanges && (hasChanges === true || hasChanges === false)) {
      this.draftHasChanges = hasChanges;
    }

    if (resetChanges) {
      this.draftHasChanges = false;
    }

    this.validateDraft();
    this.emitChange();
  },

  RECEIVE_PROCTOR_CONFIG_DRAFT_MAX_ATTEMPTS({ maxAttempts }: $TSFixMe) {
    delete this.proctorConfigDraft.maxAttempts;
    if (maxAttempts !== null && maxAttempts >= 0) {
      this.proctorConfigDraft.maxAttempts = maxAttempts;
    }

    this.draftHasChanges = true;
    this.validateDraft();
    this.emitChange();
  },

  RECEIVE_PROCTOR_CONFIG_DRAFT_TIME_LIMIT({ timeLimit }: $TSFixMe) {
    const newAttemptConfig =
      (this.proctorConfigDraft.attemptConfigurations && this.proctorConfigDraft.attemptConfigurations[0]) ||
      Object.assign({}, DEFAULT_ATTEMPT_CONFIG);

    delete newAttemptConfig.timeLimit;
    if (timeLimit !== null && timeLimit > 0) {
      newAttemptConfig.timeLimit = Object.assign({}, DEFAULT_ATTEMPT_CONFIG_TIME_LIMIT, { duration: timeLimit });
    }
    this.proctorConfigDraft.attemptConfigurations = [newAttemptConfig];
    this.draftHasChanges = true;

    this.validateDraft();
    this.emitChange();
  },

  RECEIVE_PROCTOR_CONFIG_DRAFT_MAX_SUBMISSIONS({ maxSubmissions }: $TSFixMe) {
    const newAttemptConfig =
      (this.proctorConfigDraft.attemptConfigurations && this.proctorConfigDraft.attemptConfigurations[0]) ||
      Object.assign({}, DEFAULT_ATTEMPT_CONFIG);

    delete newAttemptConfig.maxSubmissions;
    if (maxSubmissions !== null && maxSubmissions > 0) {
      newAttemptConfig.maxSubmissions = maxSubmissions;
    }
    this.proctorConfigDraft.attemptConfigurations = [newAttemptConfig];
    this.draftHasChanges = true;

    this.validateDraft();
    this.emitChange();
  },

  INITIALIZE_PROCTOR_CONFIG_DRAFT({ proctorConfigDraft }: $TSFixMe) {
    this.proctorConfigDraftId = proctorConfigDraft.proctorConfigurationId;
    this.id = proctorConfigDraft.id;
    this.proctorConfigDraft = proctorConfigDraft.draft || {};
    this.proctorConfigDraftMetadata = proctorConfigDraft.metadata || {};
    const newAttemptConfig = Object.assign({}, DEFAULT_ATTEMPT_CONFIG);
    this.proctorConfigDraft.attemptConfigurations = [newAttemptConfig];
    this.draftHasChanges = false;
    this.validateDraft();
    this.emitChange();
  },

  CLEAR_PROCTOR_CONFIG_DRAFT() {
    delete this.proctorConfigDraft.maxAttempts;
    const newAttemptConfig = Object.assign({}, DEFAULT_ATTEMPT_CONFIG);
    this.proctorConfigDraft.attemptConfigurations = [newAttemptConfig];
    this.draftHasChanges = false;
    this.validateDraft();
    this.emitChange();
  },

  RESET_PROCTOR_CONFIG_STORE() {
    this.initialize();
    this.emitChange();
  },
};

export default ProctorConfigStore;
